import React, { useEffect, useMemo, useState } from 'react'

import { F0Box, F0Select } from '@factorialco/f0-react'
import { Dashboard, OneEmptyState, Stack } from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'
import { TicketInsightsDocument } from 'generated/resources/graphql'

import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useMyResolverTeams } from 'modules/ticketing/hooks/useMyResolverTeams'
import { useTicketingPolicies } from 'modules/ticketing/hooks/useTicketingPolicies'

import { InsightsKpiCards } from 'modules/ticketing/components/Insights/InsightsKpiCards'
import { InsightsTicketActivityChart } from 'modules/ticketing/components/Insights/InsightsTicketActivityChart'
import { InsightsResolutionTimeChart } from 'modules/ticketing/components/Insights/InsightsResolutionTimeChart'
import { InsightsResolverTable } from 'modules/ticketing/components/Insights/InsightsResolverTable'
import { InsightsOverdueChart } from 'modules/ticketing/components/Insights/InsightsOverdueChart'
import {
  trackGoToTicketingInsights,
  trackChangeInsightsTeamFilter,
  trackChangeInsightsPeriodFilter,
} from 'modules/ticketing/lib/tracking'

const getPeriodOptions = () => [
  { label: i18n.t('ticketing.inbox.insights.period.last_7_days'), value: 'last_7_days' },
  { label: i18n.t('ticketing.inbox.insights.period.last_30_days'), value: 'last_30_days' },
  { label: i18n.t('ticketing.inbox.insights.period.last_90_days'), value: 'last_90_days' },
  { label: i18n.t('ticketing.inbox.insights.period.last_12_months'), value: 'last_12_months' },
  { label: i18n.t('ticketing.inbox.insights.period.all_time'), value: 'all_time' },
]

export const InsightsTab: React.FC = () => {
  const { has_dev_ai_ticketing_core, loadingFeatures } = useFeatures([
    Features.dev_ai_ticketing_core,
  ])

  const { canViewInsights, error: policiesError } = useTicketingPolicies()
  const { teams, loading: loadingTeams, error: teamsError } = useMyResolverTeams()

  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(undefined)
  const [selectedPeriod, setSelectedPeriod] = useState<string>('last_30_days')

  useEffect(() => {
    trackGoToTicketingInsights()
  }, [])

  // Default to first team when loaded
  const teamId = useMemo(() => {
    if (selectedTeamId) return selectedTeamId
    return teams[0]?.value
  }, [selectedTeamId, teams])

  const {
    data,
    loading: loadingData,
    error,
  } = useQuery(TicketInsightsDocument, {
    variables: { teamId: teamId ?? '', period: selectedPeriod },
    skip: !teamId,
    fetchPolicy: 'cache-and-network',
  })

  const insights = data?.ticketing?.ticketInsights
  const insight = insights?.[0]
  const kpi = insight?.kpiMetrics
  const isAllTime = selectedPeriod === 'all_time'

  return (
    <ResponsivePage.Body
      variant="wide"
      height="full"
      loading={loadingFeatures}
      authorized={has_dev_ai_ticketing_core && canViewInsights}
      error={error ?? teamsError ?? policiesError}
    >
      {!loadingFeatures && !has_dev_ai_ticketing_core ? (
        <Stack gap="4" paddingY="p-4">
          <OneEmptyState
            title={i18n.t('ticketing.inbox.no_access_title')}
            description={i18n.t('ticketing.inbox.no_access_description')}
          />
        </Stack>
      ) : (
        <Stack gap="4" paddingY="p-4">
          {/* Filters */}
          <F0Box display="flex" gap="md" alignItems="end">
            <F0Select
              label={i18n.t('ticketing.inbox.insights.filters.team')}
              value={teamId}
              onChange={(value: string) => {
                setSelectedTeamId(value)
                trackChangeInsightsTeamFilter({ team_id: value })
              }}
              options={teams.map((t) => ({
                label: t.label,
                value: t.value,
                avatar: {
                  type: 'team' as const,
                  name: t.label || '',
                  src: '',
                },
              }))}
            />
            <F0Select
              label={i18n.t('ticketing.inbox.insights.filters.period')}
              value={selectedPeriod}
              onChange={(value: string) => {
                setSelectedPeriod(value)
                trackChangeInsightsPeriodFilter({ period: value })
              }}
              options={getPeriodOptions()}
            />
          </F0Box>

          {/* Content */}
          {(loadingData || loadingTeams) && !insight ? (
            <Dashboard.Skeleton />
          ) : !insight || !kpi ? (
            <OneEmptyState
              emoji="📊"
              title={i18n.t('ticketing.inbox.insights.empty_title')}
              description={i18n.t('ticketing.inbox.insights.empty_description')}
            />
          ) : (
            <Stack gap="4">
              {/* Row 1: KPI cards */}
              <InsightsKpiCards
                assignedCount={kpi.assignedCount}
                assignedCountPrevious={kpi.assignedCountPrevious}
                resolvedCount={kpi.resolvedCount}
                resolvedCountPrevious={kpi.resolvedCountPrevious}
                inProgressCount={kpi.inProgressCount}
                inProgressCountPrevious={kpi.inProgressCountPrevious}
                averageResolutionTimeSeconds={kpi.averageResolutionTimeSeconds}
                averageResolutionTimeSecondsPrevious={kpi.averageResolutionTimeSecondsPrevious}
                slaCompliancePercent={kpi.slaCompliancePercent}
                slaCompliancePercentPrevious={kpi.slaCompliancePercentPrevious}
                isAllTime={isAllTime}
              />
              {/* Row 2: Charts side by side */}
              <Dashboard>
                <InsightsTicketActivityChart
                  data={insight.ticketActivity}
                  period={selectedPeriod}
                />
                <InsightsResolutionTimeChart
                  data={insight.resolutionTimeSeries}
                  period={selectedPeriod}
                />
                <InsightsOverdueChart data={insight.overdueTimeSeries} period={selectedPeriod} />
              </Dashboard>
              {/* Row 3: Resolver table full width */}
              <InsightsResolverTable data={insight.resolverProductivity} />
            </Stack>
          )}
        </Stack>
      )}
    </ResponsivePage.Body>
  )
}

export default InsightsTab
